import React from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { Location, useLocation } from 'react-router-dom';

import { Integration } from '@futrli-sdk/app-store';
import { Organisation } from '@futrli-sdk/organisations';
import {
  getOrganisationById,
  getValueFromQuery,
  isPending,
  organisationByIdSelector,
  QUERY_PARAMS
} from '@futrli/shared-services';
import { UIDefaultErrorMessage } from '@futrli/ui-components';
import { useIntegrationsView } from '@futrli/views/hooks/useIntegrationsView';

import { StoreState } from '../../core/redux-utils/types';
import { OnboardingIntegrations } from '../components/onboarding-intergrations';

interface StateToProps {
  readonly workspaceId: string;
  readonly organisation: Organisation | null;
  readonly isOrgLoading: boolean;
}

interface IntegrationsProps {
  readonly integrations: Integration[] | undefined;
  readonly isIntegrationsLoading: boolean;
  readonly integrationsError: Error | null;
}

interface DispatchToProps {
  readonly getOrganisationById: typeof getOrganisationById;
}

export type PropsWithoutRouter = StateToProps & DispatchToProps & IntegrationsProps;

type Props = { location: Location } & PropsWithoutRouter;

export class OnboardingIntegrationsContainerBase extends React.PureComponent<Props> {
  public componentDidMount(): void {
    const { organisation, location } = this.props;
    const { organisationId, fromRedirect } = this.getValuesFromQuery(location);

    if (fromRedirect === 'true' && (!organisation || organisation.id !== organisationId)) {
      this.loadOrganisation();
    }
  }

  public render(): JSX.Element {
    const { workspaceId, organisation, isOrgLoading, isIntegrationsLoading, integrations, integrationsError, location } = this.props;
    const { fromRedirect } = this.getValuesFromQuery(location);

    if (isOrgLoading || isIntegrationsLoading) {
      return <React.Fragment />;
    }

    if ((fromRedirect === 'true' && isEmpty(organisation)) || integrationsError || !integrations?.length) {
      return <UIDefaultErrorMessage />;
    }

    return (
      <OnboardingIntegrations
        workspaceId={workspaceId}
        organisation={organisation}
        integrations={integrations}
      />
    );
  }

  private getValuesFromQuery(location: Location) {
    const workspaceId = getValueFromQuery(location, QUERY_PARAMS.WORKSPACE_ID) as string;
    const organisationId = getValueFromQuery(location, QUERY_PARAMS.ORG_ID) as string;
    const fromRedirect = getValueFromQuery(location, QUERY_PARAMS.FROM_REDIRECT) as string;

    return {
      workspaceId,
      organisationId,
      fromRedirect
    };
  }

  /**
   * Loads the Organisation from the server.
   * @throws {Error}
   */
  private async loadOrganisation(): Promise<void> {
    const { workspaceId, organisationId } = this.getValuesFromQuery(this.props.location);

    this.props.getOrganisationById(workspaceId, organisationId);
  }
}

const mapStateToProps = (state: StoreState, ownProps: { location: Location }): StateToProps => {
  const workspaceId = getValueFromQuery(ownProps.location, QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = getValueFromQuery(ownProps.location, QUERY_PARAMS.ORG_ID);
  const { normalised } = state;

  return {
    workspaceId: workspaceId as string,
    organisation: organisationByIdSelector(normalised, workspaceId as string, organisationId as string),
    isOrgLoading: isPending(state.loadingRequests.FETCH_FROM_REMOTE_GET_ORGANISATION_BY_ID)
  };
};

const mapDispatchToProps: DispatchToProps = {
  getOrganisationById
};

export const OnboardingIntegrationsContainerWS = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingIntegrationsContainerBase);

export const OnboardingIntegrationsContainer = () => {
  const location = useLocation();
  const { integrations, isLoading, errorLoading } = useIntegrationsView();
  return (
    <OnboardingIntegrationsContainerWS
      location={location}
      integrations={integrations}
      isIntegrationsLoading={isLoading}
      integrationsError={errorLoading}
    />
  );
};
